import React, { useState } from "react";
import { useHistory } from "react-router";
import { useUnmount } from "react-use";
import {
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Form, Formik, FormikProps } from "formik";
import { uniq, without } from "lodash";
import * as yup from "yup";

import FormikFormField from "components/shared/FormikFormField";
import { SEARCH_TARGETS } from "helpers/apis";
import useAxios from "helpers/axios";
import useApp from "hooks/useApp";
import { colors } from "theme";
import { IDiseaseType } from "types/diseaseType";
import { IForm, errors as CustomErrors } from "types/form";
import { IWorkflow } from "types/workflow";

interface ITextEntryForm extends IForm {
  workflowId: number | string;
  diseaseTypeId: number | string;
  genes: string[];
}

const useStyles = makeStyles((theme) => ({
  fieldset: {
    border: "none",
    marginInlineStart: 0,
    marginInlineEnd: 0,
    paddingBlockStart: 0,
    paddingInlineStart: 0,
    paddingInlineEnd: 0,
    paddingBlockEnd: 0,
    minInlineSize: "inherit",
  },
  legend: {
    marginBottom: theme.spacing(2),
    paddingInlineStart: 0,
    paddingInlineEnd: 0,
  },
}));

const validationSchema = yup.object({
  workflowId: yup.number().required(CustomErrors.required).positive(),
  diseaseTypeId: yup.number().required(CustomErrors.required).positive(),
  genes: yup
    .array()
    .of(yup.string())
    .required(CustomErrors.required)
    .min(1, "Please enter at least 1 target"),
});

const TextEntryForm = ({
  diseaseTypes,
  workflows,
  formData: props,
  setDiseaseTypeId,
  setWorkflowId,
}: {
  formData: FormikProps<ITextEntryForm>;
  diseaseTypes: IDiseaseType[];
  workflows: IWorkflow[];
  setDiseaseTypeId: Function;
  setWorkflowId: Function;
}) => {
  const {
    dirty,
    errors,
    isValid,
    setFieldError,
    setFieldTouched,
    setFieldValue,
    touched,
    values,
  } = props;
  const [symbol, setSymbol] = useState<string>("");
  const [notFoundGenes, setNotFoundGenes] = useState<string[]>([]);
  const history = useHistory();
  const onSymbolChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSymbol(event.target.value);
  };
  const classes = useStyles();

  const { fetchData, loading } = useAxios({
    config: { url: SEARCH_TARGETS },
    callOnInit: false,
  });

  const onViewMatches = () => {
    setFieldTouched("genes", true);

    fetchData(
      {
        params: {
          targets: values.genes.join(","),
        },
      },
      ({ response }: any) => {
        if (response && Array.isArray(response.foundGenes)) {
          if (
            response.notFoundGenes.length > 0 ||
            response.foundGenes.length === 0
          ) {
            setFieldError(
              "genes",
              CustomErrors.noFoundGenes(response.notFoundGenes)
            );
            setNotFoundGenes(response.notFoundGenes);
          } else {
            history.push(
              `/evaluate-panel-options?workflow=${values.workflowId}&diseaseType=${values.diseaseTypeId}&foundGenes=${response.foundGenes}`
            );
          }
        }
      }
    );
  };

  useUnmount(() => {
    setWorkflowId(values.workflowId);
    setDiseaseTypeId(values.diseaseTypeId);
  });

  return (
    <Form>
      <Box
        component="fieldset"
        my={3}
        display="flex"
        flexDirection="column"
        alignItems="center"
        className={classes.fieldset}
      >
        <Typography
          variant="body1"
          component="legend"
          className={classes.legend}
        >
          Please select and enter genes to view matches
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormikFormField<ITextEntryForm>
              name="workflowId"
              label="Select Workflow"
              {...props}
              textFieldProps={{
                select: true,
                helperText: "",
                children: workflows.map((workflow: IWorkflow) => (
                  <MenuItem key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </MenuItem>
                )),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormikFormField
              name="diseaseTypeId"
              label="Select Disease Type"
              {...props}
              textFieldProps={{
                select: true,
                helperText: "",
                children: diseaseTypes.map((diseaseType: IDiseaseType) => (
                  <MenuItem key={diseaseType.id} value={diseaseType.id}>
                    {diseaseType.name}
                  </MenuItem>
                )),
              }}
            />
          </Grid>
        </Grid>

        <Box
          width={1}
          minHeight="227px"
          boxSizing="border-box"
          pr={1.5}
          py={1.5}
          pl={2}
          mt={3}
          bgcolor={colors.grey[600]}
          display="flex"
          flexDirection="column"
        >
          <Box mb={1}>
            <TextField
              label="Enter HGNC symbols/fusions"
              required
              fullWidth
              onKeyPress={(event: React.KeyboardEvent) => {
                if (event.key === "Enter" && symbol) {
                  // Multiple symbols might be given separated by comma
                  const symbols = symbol
                    .split(",")
                    .map((name: string) => name.trim());
                  setFieldValue("genes", uniq([...values.genes, ...symbols]));
                  setSymbol("");
                }
              }}
              value={symbol}
              onChange={onSymbolChange}
            />
          </Box>
          <Grid container spacing={1}>
            {values.genes.map((symbol: string) => (
              <Grid item key={symbol}>
                <Box my={0.5}>
                  <Chip
                    label={symbol}
                    onDelete={() => {
                      setFieldValue("genes", without(values.genes, symbol));
                    }}
                    className={
                      notFoundGenes.includes(symbol) ? "error" : ""
                    }
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {errors.genes && touched.genes && (
          <Box mt={1} display="flex" width={1}>
            <Typography variant="body2" color="error">
              {errors.genes}
            </Typography>
          </Box>
        )}

        <Box mt={3}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            disabled={!dirty || !isValid || loading}
            onClick={onViewMatches}
          >
            View matches
          </Button>
        </Box>
      </Box>
    </Form>
  );
};

const TextEntry = ({
  workflowId,
  setWorkflowId,
  diseaseTypeId,
  setDiseaseTypeId,
}: {
  workflowId: number | string;
  diseaseTypeId: number | string;
  setWorkflowId: Function;
  setDiseaseTypeId: Function;
}) => {
  const { diseaseTypes, workflows } = useApp();
  const initialValues: ITextEntryForm = React.useMemo(
    () => ({
      workflowId: workflowId,
      diseaseTypeId: diseaseTypeId,
      genes: [],
    }),
    [workflowId, diseaseTypeId]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={() => {}}
      validationSchema={validationSchema}
    >
      {(formData: FormikProps<ITextEntryForm>) => (
        <TextEntryForm
          formData={formData}
          diseaseTypes={diseaseTypes}
          workflows={workflows}
          setWorkflowId={setWorkflowId}
          setDiseaseTypeId={setDiseaseTypeId}
        />
      )}
    </Formik>
  );
};

export default React.memo(TextEntry);
